// SCRUM-850 — color-coded environment badge for the topbar. Hidden on
// prod (is_production=true). On non-prod envs, renders a pill with the
// display name, a tooltip explaining the env, and aria-label for screen
// readers. On viewports <=576px collapses to a single initial.
//
// Spec: ENV_BADGE_TOPNAV_SPEC.md
// Test: testing/tests/env-badge/env-badge.spec.ts (SCRUM-851)
import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { EnvironmentService, EnvInfo } from '../services/environment.service';

@Component({
  selector: 'app-env-badge',
  standalone: true,
  imports: [CommonModule, TooltipModule],
  template: `
    <span
      *ngIf="env && !env.is_production"
      class="env-badge env-badge-{{ env.name }}"
      data-testid="env-badge"
      role="status"
      [attr.aria-label]="'Current environment: ' + env.display_name"
      [pTooltip]="
        'You are viewing the ' + env.display_name + ' environment. Production is alphagroves.com.'
      "
      tooltipPosition="bottom"
    >
      <span class="env-badge-full">{{ env.display_name | uppercase }}</span>
      <span class="env-badge-initial">{{ initial }}</span>
    </span>
  `,
  styles: [
    `
      .env-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 700;
        padding: 3px 9px;
        border-radius: 999px;
        letter-spacing: 0.06em;
        white-space: nowrap;
        cursor: help;
        user-select: none;
      }
      /* Color tokens kept in sync with system-status.component.ts env-badge-*. */
      .env-badge-dev { background: rgba(99, 102, 241, 0.15); color: #4f46e5; }
      .env-badge-uat { background: rgba(245, 158, 11, 0.15); color: #b45309; }
      /* Any non-mapped non-prod env (hetzner-test, dr1, etc.) falls into a neutral gray. */
      .env-badge:not(.env-badge-dev):not(.env-badge-uat) {
        background: rgba(107, 114, 128, 0.15);
        color: #374151;
      }
      .env-badge-initial { display: none; }

      /* Compact rendering on mobile — single initial letter only. Tooltip
         retains the full label. */
      @media (max-width: 576px) {
        .env-badge-full { display: none; }
        .env-badge-initial { display: inline; }
        .env-badge { padding: 3px 8px; font-size: 12px; }
      }
    `,
  ],
})
export class EnvBadgeComponent implements OnInit, OnDestroy {
  env: EnvInfo | null = null;
  private sub?: Subscription;

  constructor(private readonly envSvc: EnvironmentService) {}

  ngOnInit(): void {
    this.sub = this.envSvc.env$.subscribe((info) => {
      this.env = info;
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get initial(): string {
    return this.env?.display_name?.[0]?.toUpperCase() ?? '?';
  }
}
